import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { useAudio } from "../contexts/AudioContext";
import { useAuth } from "../contexts/AuthContext";
import { Play, Bookmark, BookmarkCheck, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Names() {
  const [names, setNames] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [bookmarks, setBookmarks] = useState(new Set());
  const [lang, setLang] = useState("sanskrit"); // sanskrit | hindi | english
  const { playName, currentName } = useAudio();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get(`/names?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => { setNames(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    if (!user) { setBookmarks(new Set()); return; }
    api.get("/bookmarks").then((r) => setBookmarks(new Set(r.data.items.map((b) => b.name_id))));
  }, [user]);

  const toggleBookmark = async (nameId) => {
    if (!user) return toast.error("Sign in to bookmark names");
    try {
      if (bookmarks.has(nameId)) {
        await api.delete(`/bookmarks/${nameId}`);
        setBookmarks((prev) => { const n = new Set(prev); n.delete(nameId); return n; });
        toast.success("Removed from bookmarks");
      } else {
        await api.post("/bookmarks", { name_id: nameId });
        setBookmarks((prev) => new Set(prev).add(nameId));
        toast.success("Added to bookmarks");
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="names-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-3xl">
        <div className="overline">॥ शिव सहस्रनाम ॥</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">The 1008 Names of Shiva</h1>
        <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
          Each name is a doorway. Tap the play button to hear the Om-chant companion, or bookmark to return later. Sanskrit, Hindi and English are offered side-by-side.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#63534E]" />
          <Input
            data-testid="names-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or meaning…"
            className="pl-9 bg-[#FFFDF9] border-[#D4AF37]/40 h-11 rounded-full"
          />
        </div>
        <div className="flex gap-2">
          {[
            { k: "sanskrit", l: "संस्कृत" },
            { k: "hindi", l: "हिन्दी" },
            { k: "english", l: "English" },
          ].map((o) => (
            <button
              key={o.k}
              data-testid={`lang-toggle-${o.k}`}
              onClick={() => setLang(o.k)}
              className={`px-4 h-11 rounded-full text-sm font-semibold transition-sacred border ${lang === o.k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40 hover:bg-[#F3E5AB]/40"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <div className="text-sm text-[#63534E] font-mono ml-auto">Showing {names.length} of {total}</div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#63534E]">Loading sacred names…</div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {names.map((n) => {
            const isActive = currentName?.id === n.id;
            const isBookmarked = bookmarks.has(n.id);
            return (
              <div
                key={n.id}
                data-testid={`shiva-name-card-${n.number}`}
                className={`bg-[#FFFDF9] sacred-border rounded-2xl p-6 transition-sacred relative ${isActive ? "ring-2 ring-[#D4AF37]" : "gold-glow"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-mono text-xs text-[#8C6D3B]">#{String(n.number).padStart(4, "0")}</div>
                  <button
                    data-testid={`bookmark-btn-${n.number}`}
                    onClick={() => toggleBookmark(n.id)}
                    className="p-1.5 rounded-full hover:bg-[#F3E5AB]/50 transition-sacred"
                    aria-label="Bookmark"
                  >
                    {isBookmarked ? <BookmarkCheck className="w-4 h-4 text-[#E65100]" /> : <Bookmark className="w-4 h-4 text-[#63534E]" />}
                  </button>
                </div>
                <div className={`mt-3 min-h-[72px] ${lang === "sanskrit" ? "font-devanagari text-2xl text-[#4A0E17]" : lang === "hindi" ? "font-devanagari text-2xl text-[#4A0E17]" : "font-serif text-2xl text-[#4A0E17]"}`}>
                  {lang === "sanskrit" && n.sanskrit}
                  {lang === "hindi" && n.hindi}
                  {lang === "english" && n.english}
                </div>
                <div className="italic text-sm text-[#63534E] mt-2">{n.transliteration}</div>
                <p className="text-sm text-[#1F1412] mt-3 leading-relaxed line-clamp-3">{n.meaning}</p>
                <button
                  data-testid={`play-name-${n.number}`}
                  onClick={() => playName(n)}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#E65100] hover:text-[#4A0E17] transition-sacred"
                >
                  <span className="w-9 h-9 rounded-full bg-[#4A0E17] text-[#D4AF37] flex items-center justify-center">
                    <Play className="w-4 h-4 ml-0.5" />
                  </span>
                  Chant this name
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
